import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import styled from 'styled-components';

import { DealPipelineInvestmentsQuery, WorkflowInstanceStateCompletionHistoryQuery } from '@/__generated__/graphql';
import { Loader } from '@/components/loaders/loader';
import { DEAL_PIPELINE_INVESTMENT_WORKFLOW_COMPLETED_STATE_HISTORY_QUERY } from '../queries';
import { CompletedStatusRow } from './CompletedStatusRow';
import { NextStepStatusRow } from './NextStepStatusRow';
import { TerminatedStatusRow } from './TerminatedStatusRow';

interface StepsListProps {
  row: DealPipelineInvestmentsQuery['Investments']['data'][number];
  initialDate: Date;
  isMobile?: boolean;
  handleStepsListReady?: ({ isReady }: { isReady: boolean }) => void;
}

interface CompletedStep {
  label: string;
  date: Date | null;
}

export const StepsList = ({ row, initialDate, isMobile = false, handleStepsListReady = () => {} }: StepsListProps) => {
  const workflowInstanceId = row?.workflowInstance?.id;
  const currentStateLabel = row?.workflowInstance?.currentState?.label;
  const isTerminated = row.workflowInstance?.currentState?.name === 'terminated';

  const { data: historyQueryData, loading: historyLoading } = useQuery<WorkflowInstanceStateCompletionHistoryQuery>(
    DEAL_PIPELINE_INVESTMENT_WORKFLOW_COMPLETED_STATE_HISTORY_QUERY,
    {
      skip: !workflowInstanceId,
      variables: {
        instanceId: workflowInstanceId
      }
    }
  );

  useEffect(() => {
    if (!historyLoading) {
      handleStepsListReady({ isReady: true });
    }
    return () => {
      handleStepsListReady({ isReady: false });
    };
  }, [historyLoading, handleStepsListReady]);

  if (historyLoading) {
    return <Loader />;
  }

  const historyData = historyQueryData?.workflowInstanceStateCompletionHistory || [];

  // Build completed steps from history
  const completedSteps: CompletedStep[] = historyData.map((transition, index) => {
    // For the first transition, use initialDate if no previous date exists
    const date = index === 0 ? initialDate : new Date(historyData[index - 1].toState?.updatedAt);
    return {
      label: transition.fromState?.label || transition.fromState?.name || '',
      date
    };
  });

  // Add the last toState as a completed step if there's history
  if (historyData.length > 0) {
    const lastTransition = historyData[historyData.length - 1];
    completedSteps.push({
      label: lastTransition.toState?.label || lastTransition.toState?.name || '',
      date: new Date(lastTransition.toState?.updatedAt)
    });
  }

  // Filter out the current state from completed steps (it will be shown as "Current:")
  const filteredCompletedSteps = completedSteps.filter((step) => step.label.toLowerCase() !== currentStateLabel?.toLowerCase());

  const hasSteps = filteredCompletedSteps.length > 0 || currentStateLabel;

  if (!hasSteps) {
    return <NoStatuses>There is no statuses</NoStatuses>;
  }

  return (
    <Container>
      {filteredCompletedSteps.map((step, index) => (
        <CompletedStatusRow key={`completed-${step.label}-${index}`} isMobile={isMobile} status={step.label} date={step.date} />
      ))}
      {currentStateLabel && !isTerminated && <NextStepStatusRow isMobile={isMobile} status={currentStateLabel} nextStepTitle="Current:" />}
      {isTerminated && <TerminatedStatusRow status="Terminated" date={row.updatedAt} isMobile={isMobile} />}
    </Container>
  );
};

const NoStatuses = styled.span`
  text-align: left;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 3px;

  * {
    white-space: nowrap;
  }
`;

export const Status = styled.span<{ color: string; isMobile?: boolean }>`
  display: inline-block;
  width: ${({ isMobile }) => (isMobile ? '70%' : '100%')};
  overflow: ${({ isMobile }) => (isMobile ? 'hidden' : 'none')};
  text-overflow: ${({ isMobile }) => (isMobile ? 'ellipsis' : 'none')};
  user-select: ${({ isMobile }) => (isMobile ? 'none' : 'auto')};
  color: ${({ color }) => color};
  padding-left: 10px;
  font-family: 'Blinker', serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
`;

export const StatusIconWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
